import { Paper, Typography, Divider, Stack, ListItem, ListItemButton, ListItemText, LinearProgress } from '@mui/material';
import { CustomFieldDto, CustomFieldOptionDto, ReportFieldFilterInput, ReportParametersInput, useFindThemesWithSpecificationsQuery } from 'gql';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { FilterInput } from '../FilterInput';
import { LookupInput } from 'components';

type CustomField = Pick<CustomFieldDto, 'id' | 'name' | 'customFieldType' | 'systemFieldId'> & {
  options: Pick<CustomFieldOptionDto, 'id' | 'name'>[];
};

interface Props {
  fields: CustomField[];
  loading?: boolean;
  disabled?: boolean;
}

export const ReportRealitiesSection: React.FC<Props> = ({ disabled, fields, loading }) => {
  const { formatMessage } = useIntl();

  const { control, setValue } = useFormContext<ReportParametersInput>();
  const { fields: fieldFilters, append, update } = useFieldArray({ control, name: 'filters.realitiesFieldFilters' });
  const selectedThemeIds = useWatch({ control, name: 'filters.themeIds' });
  const selectedSpecificationIds = useWatch({ control, name: 'filters.specificationIds' });

  const onChange = (customFieldFilterInput: ReportFieldFilterInput) => {
    const index = fieldFilters.findIndex(f => f.customFieldId === customFieldFilterInput.customFieldId);
    if (index === -1) {
      append(customFieldFilterInput);
    } else {
      update(index, customFieldFilterInput);
    }
  };

  const { data: themesData } = useFindThemesWithSpecificationsQuery();
  const themes = useMemo(() => themesData?.themes ?? [], [themesData?.themes]);

  const availableSpecifications = useMemo(() => themes.filter(theme => selectedThemeIds?.includes(theme.id)).flatMap(theme => theme.specifications), [themes, selectedThemeIds]);

  useEffect(() => {
    setValue('filters.specificationIds', availableSpecifications.filter(spec => selectedSpecificationIds?.includes(spec.id)).map(spec => spec.id));
  }, [availableSpecifications, setValue]);

  return (
    <Paper>
      <Typography variant='h6' p={2} pb={1}>{formatMessage({ id: 'Realities' })}</Typography>

      <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
      <Divider />

      <Stack p={2} spacing={2}>
        <Controller
          control={control}
          name='filters.themeIds'
          render={({ field }) => (
            <LookupInput
              multiple
              label={formatMessage({ id: 'Themes' })}
              availableValues={themes}
              getKey={theme => theme.id}
              getLabel={theme => theme.name}
              searchFilter={(theme, searchValue) => theme.name.includes(searchValue)}
              renderListItem={(theme, selected, onClick) => (
                <ListItem disablePadding>
                  <ListItemButton selected={selected} onClick={onClick}>
                    <ListItemText
                      primary={theme.name}
                    />
                  </ListItemButton>
                </ListItem>
              )}
              selectedValues={themes.filter(theme => field.value?.includes(theme.id))}
              onChange={themes => field.onChange(themes.map(theme => theme.id))}
              disabled={disabled}
            />
          )}
        />

        {availableSpecifications.length > 0 && (
          <Controller
            control={control}
            name='filters.specificationIds'
            render={({ field }) => (
              <LookupInput
                multiple
                label={formatMessage({ id: 'Specifications' })}
                availableValues={availableSpecifications}
                getKey={spec => spec.id}
                getLabel={spec => spec.name}
                searchFilter={(spec, searchValue) => spec.name.includes(searchValue)}
                renderListItem={(spec, selected, onClick) => (
                  <ListItem disablePadding>
                    <ListItemButton selected={selected} onClick={onClick}>
                      <ListItemText
                        primary={spec.name}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                selectedValues={availableSpecifications.filter(spec => field.value?.includes(spec.id))}
                onChange={specs => field.onChange(specs.map(spec => spec.id))}
                disabled={disabled}
              />
            )}
          />
        )}

        {fields.map(field => (
          <FilterInput
            key={field.id}
            customField={field}
            value={fieldFilters.find(f => f.customFieldId === field.id) ?? { customFieldId: field.id, systemFieldId: field.systemFieldId }}
            onChange={onChange}
            disabled={disabled}
          />
        ))}
      </Stack>
    </Paper>
  );
};